import { Appbar, Icon, Text } from 'react-native-paper'
import { StackHeaderProps, StackNavigationProp } from '@react-navigation/stack'
import { AppNavigation, ReplayNavigatorParams } from '../../../navigation/types'
import { ActivityIndicator, View } from 'react-native'
import { useTheme } from '../../../hooks/theme.hook'
import { TouchableIconWithToolTip } from '../../../utils/meldd-tooltip'
import { useTranslation } from 'react-i18next'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { TargetType } from '../../../generated/graphql'
import { IconExit } from '../../../utils/meldd-icons'
import { useReplayContext } from '../../../contexts/replay.context'
import { ReplaySlider } from './components/replay.slider'
import { ReplayControls } from './components/replay-controllers'
import { useEffect, useLayoutEffect } from 'react'
import { useSnapshotStore } from '../../../services/snapshots/snapshot.store.hook'
import { ReplyActionTitle, TimelineTimestamp } from './replay.screen'
import { useIsMobileDevice } from '../../../hooks/is-mobile.hook'
import Row from '../../../@ui/containers/grid/row'
import Column from '../../../@ui/containers/grid/column'

export interface ReplayNavigationBarProps extends StackHeaderProps {
  navigation: StackNavigationProp<ReplayNavigatorParams>
}

export const ExitButton = ({ nodeId }: { nodeId: string }) => {
  const navigation = useNavigation<AppNavigation>()
  const { t } = useTranslation('replay')
  return (
    <View>
      <TouchableIconWithToolTip
        tooltip={t('exit')}
        onPress={() => {
          navigation.getParent()?.goBack()
        }}
        mode="text"
        icon={<Icon source={IconExit} size={24} />}
      />
    </View>
  )
}

const HeaderDesktopLayout = ({
  ReplayComponent,
  ExitButton,
  SliderComponent,
  InfoComponent,
}: {
  ReplayComponent: React.ReactNode
  ExitButton: React.ReactNode
  SliderComponent: React.ReactNode
  InfoComponent: React.ReactNode
}) => {
  return (
    <Column style={{ flex: 1, marginHorizontal: 18, marginTop: 8 }}>
      <Row wrap alignItems="center" flex={1}>
        {ReplayComponent}
        <View style={{ flex: 1, paddingHorizontal: 12 }}>
          {SliderComponent}
        </View>
        {ExitButton}
      </Row>
      <Row
        justifyContent="center"
        alignItems="flex-start"
        style={{ marginBottom: 18 }}
      >
        {InfoComponent}
      </Row>
    </Column>
  )
}

const HeaderMobileLayout = ({
  ReplayComponent,
  ExitButton,
  SliderComponent,
  InfoComponent,
}: {
  ReplayComponent: React.ReactNode
  ExitButton: React.ReactNode
  SliderComponent: React.ReactNode
  InfoComponent: React.ReactNode
}) => {
  return (
    <Column
      flex={1}
      width="100%"
      style={{
        marginHorizontal: 18,
      }}
    >
      <Row flex={1} alignItems="center" justifyContent="space-between">
        {ReplayComponent}
        <Row flex={1} style={{ marginHorizontal: 2 }}>
          {InfoComponent}
        </Row>
        {ExitButton}
      </Row>
      <Row>{SliderComponent}</Row>
    </Column>
  )
}

const config: Record<
  string,
  (route: RouteProp<ReplayNavigatorParams>) => {
    id: string
    type: TargetType
  }
> = {
  ReplayPerspective: (route) => ({
    id: (route.params as ReplayNavigatorParams['ReplayPerspective']).articleId,
    type: TargetType.Article,
  }),
  ReplayClusterCards: (route) => ({
    id: (route.params as ReplayNavigatorParams['ReplayClusterCards']).clusterId,
    type: TargetType.Cluster,
  }),
  ReplayPerspectiveContributions: (route) => ({
    id: (
      route.params as ReplayNavigatorParams['ReplayPerspectiveContributions']
    ).articleId,
    type: TargetType.Article,
  }),
  ReplayContribution: (route) => ({
    id: (route.params as ReplayNavigatorParams['ReplayContribution'])
      .contributionId,
    type: TargetType.Contribution,
  }),
}

export function ReplayNavigationBar({ navigation }: ReplayNavigationBarProps) {
  const theme = useTheme()
  const route = useRoute<RouteProp<ReplayNavigatorParams>>()
  const { nodeId, setSnapshotTick, loading, snapshot } = useSnapshotStore()

  const {
    isPlaying,
    sliderTick,
    totalItems,
    timelineItems,
    startPlayback,
    stopPlayback,
    seekToIndex,
    resetReplay,
    setFilter,
  } = useReplayContext(nodeId)
  const isMobile = useIsMobileDevice()

  /**
   * This will fire when we load the screen from another screen
   * This is the first time we enter the stack navigation
   * Will reset the replay if required (we load a new node)
   * or use the data from the cache (we reload or we initialize the cache in the previous screen)
   */
  useLayoutEffect(() => {
    if (!nodeId) {
      navigation.goBack()
      return
    }

    resetReplay(nodeId, snapshot.tick)
    // setTimeout(() => {
    //   startPlayback()
    // }, 500)
  }, [])

  /**
   * Every time the slider tick changes, this useEffect will fire
   * and update the snapshot tick.
   *
   * This can also happen if we are in a tick which items are not loaded yet
   * for that a change on the items could trigger the snapshot as well
   */
  useEffect(() => {
    if (timelineItems.length === 0) {
      return
    }

    // End of the timeline, nothing to do
    if (sliderTick >= totalItems) {
      return
    }
    setSnapshotTick(timelineItems, sliderTick)
  }, [sliderTick, timelineItems])

  /**
   * In nested elements of the tree we only saw the relevant timeline events
   */
  useEffect(() => {
    const name = route.name as keyof typeof config
    if (config[name]) {
      setFilter(config[name](route))
      return
    }

    setFilter({
      type: TargetType.Node,
      id: nodeId,
    })
  }, [route])

  const Layout = isMobile ? HeaderMobileLayout : HeaderDesktopLayout

  return (
    <Appbar.Header
      style={{
        backgroundColor: theme.colors.secondaryContainer,
        height: isMobile ? 120 : undefined,
      }}
    >
      <Layout
        ReplayComponent={
          <ReplayControls
            isPlaying={isPlaying}
            onNext={() => {
              if (sliderTick < totalItems - 1) {
                seekToIndex(sliderTick + 1, 'forward')
              }
            }}
            onPrevious={() => {
              if (sliderTick > 0) {
                seekToIndex(sliderTick - 1, 'backwards')
              }
            }}
            onPlay={startPlayback}
            onPause={stopPlayback}
          />
        }
        ExitButton={<ExitButton nodeId={nodeId} />}
        InfoComponent={
          <Text variant="bodySmall">
            {loading ? (
              <ActivityIndicator size="small" />
            ) : (
              <>
                <TimelineTimestamp item={timelineItems[sliderTick]} />{' '}
                <ReplyActionTitle item={timelineItems[sliderTick]} />
              </>
            )}
          </Text>
        }
        SliderComponent={
          <ReplaySlider
            progress={sliderTick}
            onSeek={(index) => {
              seekToIndex(index)
            }}
            totalSteps={totalItems - 1}
            isLoading={loading}
          />
        }
      />
    </Appbar.Header>
  )
}
